/**
 * THE ONE way a surface names the clinic it is showing
 * (119-CLINIC-OPERATING-SPINE-V1, Phase 2).
 *
 * Phase 2 requires every surface showing patients, visits, leads or money to
 * DISPLAY the clinic it is showing. Before this primitive two ad-hoc patterns
 * already existed for that same job (the front-desk home's "Clinic: <name>"
 * sub-line and the doctor day's own span), which is a second pattern for a
 * solved interaction -- a defect, not a variation. This is the single carrier;
 * it rides the PageHeader `scope` slot, which is already registered for exactly
 * this ("SCOPE control (clinic / all clinics chip)").
 *
 * TWO MODES, because the founder ruling separates two different questions:
 *
 *   LENS ("where am I working right now") -- the ordinary case. An operational
 *   surface reads the lens and names it. One labelled chip.
 *
 *   PARAMETER ("what am I asking about") -- an ANALYTICS surface may be
 *   parameterised to a clinic other than the lens, because comparing branches
 *   is the owner's actual job at 119 clinics. Where it is, the ruling requires
 *   it to say so ON THE GLASS, IN WORDS: this mode names the clinic being shown
 *   AND states that it differs from the current lens. It never silently
 *   rewrites the lens -- this component only reports, it holds no state.
 *
 * NEVER A RAW CLINIC ID. The label is always a name (clinicOptionLabel is the
 * one labeller); an unnamed clinic reads "Unnamed clinic", a word, never a
 * number. A bare unlabelled value is not shippable on this product.
 */

export type ClinicScopeMode = 'lens' | 'parameter';

export interface ClinicScopeLabelProps {
  /** The clinic being shown, already a human label (never an id). */
  readonly label: string;
  /** 'lens' = this surface follows the global lens (the default).
   *  'parameter' = an analytics surface deliberately showing another clinic. */
  readonly mode?: ClinicScopeMode;
  /** In 'parameter' mode, the lens's own label, so the divergence is stated. */
  readonly lensLabel?: string | null;
  readonly testId?: string;
}

/** The chip's leading word. A state always has a word on this product. */
export const CLINIC_SCOPE_TERM = 'Clinic';

/**
 * The sentence a parameterised analytics surface must show. Pure so the wording
 * is testable without a DOM. Returns null when there is nothing to disclose --
 * no lens, or the parameter agrees with the lens (then it is not a divergence
 * and the plain chip tells the whole truth).
 */
export function clinicScopeDivergenceLine(
  shownLabel: string,
  lensLabel: string | null | undefined,
): string | null {
  if (lensLabel === null || lensLabel === undefined || lensLabel === '') return null;
  if (lensLabel === shownLabel) return null;
  return `Showing ${shownLabel}. Your current clinic is ${lensLabel}.`;
}

export default function ClinicScopeLabel({
  label,
  mode = 'lens',
  lensLabel = null,
  testId,
}: ClinicScopeLabelProps): JSX.Element {
  const divergence =
    mode === 'parameter' ? clinicScopeDivergenceLine(label, lensLabel) : null;
  return (
    <span className="ac-clinicscope" data-testid={testId}>
      <span className="ac-clinicscope__chip">
        <span className="ac-clinicscope__term">{CLINIC_SCOPE_TERM}</span>
        <span className="ac-clinicscope__value">{label}</span>
      </span>
      {divergence !== null ? (
        <span
          className="ac-clinicscope__note"
          data-testid={testId !== undefined ? `${testId}-divergence` : undefined}
        >
          {divergence}
        </span>
      ) : null}
    </span>
  );
}
